import type { FieldSeason, FieldSeasonData, Season } from '@core/models/seasons';
import type { YieldAnalysis } from '@core/models/yieldAnalysis/yieldAnalysis.models';
import type { CropSeason } from '@screens/YieldAnalysis/YieldAnalysis.models';
import dayjs from '@utils/dateUtils';
import { Dictionary } from 'redux-ngrx-entity';

export function uploadedCropSeasonAdapter(cropSeason: CropSeason, orgId: string): FieldSeasonData {
  const adaptedId = `uploaded_${cropSeason.id}`;

  const year = typeof cropSeason.year === 'string' ? dayjs().year() : cropSeason.year;

  const season: Season = {
    id: adaptedId,
    org_id: orgId,
    crop_id: cropSeason.crop,
    name: cropSeason.name,
    start_date: cropSeason.startDate || new Date(year, 1, 1).toISOString(),
    end_date: cropSeason.endDate || new Date(year, 12, 31).toISOString(),
    analyticsPreviousStartDate: cropSeason.previousStartDate,
    analyticsPreviousEndDate: cropSeason.previousEndDate,
    active: false,
    deleted: false,
    hasAnalyticsYear: typeof cropSeason.year !== 'string',
    catalogCrop: {
      adapted_wk_slug: cropSeason.crop,
      id: adaptedId,
      name: [],
      localized_crops: [],
      image_url: null,
      image_url_ref: null,
      icon: null,
      specialization_type: null,
      label: null,
      scientific_name: '',
      image_updated_at: null,
      image_updated_by: null,
    },
  };

  return { id: adaptedId, season, fieldsSeason: {}, uploaded: true };
}

export function fieldSeasonToCropSeasonAdapter(
  fieldSeasonData?: FieldSeasonData,
  seasons?: Dictionary<FieldSeasonData>
) {
  if (fieldSeasonData?.uploaded) {
    const adaptedId = fieldSeasonData.season.id.replace('uploaded_', '');

    return {
      id: adaptedId,
      name: fieldSeasonData.season.name,
      crop: fieldSeasonData.season.crop_id,
      year: fieldSeasonData.season.hasAnalyticsYear
        ? dayjs(fieldSeasonData.season.start_date).year()
        : undefined,
      startDate: fieldSeasonData.season.hasAnalyticsYear
        ? undefined
        : fieldSeasonData.season.start_date,
      endDate: fieldSeasonData.season.hasAnalyticsYear
        ? undefined
        : fieldSeasonData.season.end_date,
      previousStartDate: fieldSeasonData.season.analyticsPreviousStartDate,
      previousEndDate: fieldSeasonData.season.analyticsPreviousEndDate,
    };
  }
  if (fieldSeasonData && seasons) {
    const adaptedId = fieldSeasonData.season.id.replace('uploaded_', '');

    // Find season with the same crop_id to current selected season
    // Also sort them from newest to oldest
    const previousSeasons = Object.values(seasons)
      .filter((fielsSeasons) => fielsSeasons.season.crop_id === fieldSeasonData.season.crop_id)
      .toSorted(
        (a, b) => new Date(b.season.start_date).getTime() - new Date(a.season.start_date).getTime()
      );

    // Find the index of the selected season
    const selectedSeasonIndex = previousSeasons.findIndex(
      (season) => season.season.id === fieldSeasonData.season.id
    );

    // Get the previous season
    const previousSeason = previousSeasons[selectedSeasonIndex + 1];

    const cropCode =
      fieldSeasonData.season.catalogCrop?.eppo_code ?? fieldSeasonData.season.catalogCrop?.icon;
    const crop = cropCode ?? fieldSeasonData.season.catalogCrop?.adapted_wk_slug;

    return {
      id: adaptedId,
      name: fieldSeasonData.season.name,
      crop: crop,
      year: undefined,
      startDate: fieldSeasonData.season.start_date,
      endDate: fieldSeasonData.season.end_date,
      previousStartDate: previousSeason?.season.start_date ?? '',
      previousEndDate: previousSeason?.season.end_date ?? '',
    };
  }

  return undefined;
}

export function analyticsFieldToFieldSeasonAdapter(
  field: Pick<YieldAnalysis.FieldDTO, 'id'>,
  fieldSeasonData: FieldSeasonData,
  propertyId: string
): FieldSeason {
  const { season } = fieldSeasonData;

  return {
    id: season.id,
    field_season_id: season.id,
    field_id: field.id,
    season_id: season.id,
    property_id: propertyId,
    name: season.name,
    season,
    varieties_ids: [],
    deleted: false,
    start_date: season.start_date,
    end_date: season.end_date,
    created_at: season.start_date,
    updated_at: season.start_date,
    emergency_date: null,
    harvesting_date: null,
    planting_date: null,
  };
}

export function generateCropSeasonGroups(
  seasonDatas: FieldSeasonData[]
): { [key: string]: FieldSeasonData[] } {
  return seasonDatas.reduce<{ [key: string]: FieldSeasonData[] }>((acc, seasonData) => {
    const { start_date, end_date } = seasonData.season;

    const startYear = dayjs(start_date).year();
    const endYear = dayjs(end_date).year();

    const groupKey = startYear === endYear ? `${startYear}` : `${startYear}__${endYear}`;

    if (acc[groupKey]) {
      acc[groupKey].push(seasonData);
    } else {
      acc[groupKey] = [seasonData];
    }

    return acc;
  }, {});
}
